import API_BASE from '../config';
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, CheckCircle2, Loader2, Shield } from "lucide-react";
import axios from "axios";

export default function Join() {
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();
  const token           = searchParams.get("token") || "";

  const [password,    setPassword]    = useState("");
  const [showPass,    setShowPass]    = useState(false);
  const [isLoading,   setIsLoading]   = useState(false);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid invite link. Please contact your administrator.");
    }
  }, [token]);

  const handleAccept = async () => {
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters."); return;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Password must contain at least one uppercase letter."); return;
    }
    if (!/[0-9]/.test(password)) {
      setError("Password must contain at least one number."); return;
    }
    setIsLoading(true); setError("");
    try {
      await axios.post(`${API_BASE}/api/team/accept`, { token, password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to accept invite. Please try again.");
    } finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6 font-['Inter']">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center shadow-xl">
            <span className="text-white font-black text-lg">V</span>
          </div>
          <span className="text-white font-black text-xl tracking-tighter uppercase">VicShield</span>
        </div>

        <div className="bg-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl">
          <div className="p-10">
            {success ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center
                  justify-center mx-auto mb-4">
                  <CheckCircle2 size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2">
                  Account Created
                </h2>
                <p className="text-slate-400 text-sm font-medium">
                  Redirecting you to login...
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center
                    justify-center mx-auto mb-4">
                    <Shield size={28} className="text-white" />
                  </div>
                  <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2">
                    Accept Invitation
                  </h2>
                  <p className="text-slate-400 text-sm font-medium">
                    You've been invited to join a VicShield organisation.
                    Set your password to get started.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-[9px] font-black uppercase tracking-widest
                      text-slate-400 block mb-2">
                      Set Password
                    </label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Min 8 chars, 1 uppercase, 1 number"
                        className="w-full pl-11 pr-12 py-4 bg-white/5 border border-white/10
                          rounded-2xl text-sm font-bold text-white placeholder:text-slate-500
                          placeholder:font-normal focus:outline-none focus:border-red-500 transition-all"
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400
                          hover:text-white transition-colors">
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <p className="text-xs font-bold text-red-400 text-center">{error}</p>
                  )}

                  <button
                    onClick={handleAccept}
                    disabled={isLoading || !token}
                    className="w-full bg-red-600 text-white py-4 rounded-2xl font-black
                      text-[11px] uppercase tracking-widest hover:bg-white hover:text-red-600
                      transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isLoading
                      ? <><Loader2 size={16} className="animate-spin" /> Creating Account...</>
                      : <><CheckCircle2 size={16} /> Accept & Create Account</>
                    }
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-[10px] font-bold text-slate-500 uppercase
          tracking-widest mt-6">
          Need help? Contact support@vicshield.com
        </p>
      </motion.div>
    </div>
  );
}